/**
 * Сверка ответа ПО ФОРМЕ · Ф2.3b — «грамматика форм» режима «Контекст».
 *
 * Зачем отдельный файл: это единственное место витрины, где есть настоящая ЛОГИКА
 * (а не отрисовка), и её надо уметь проверить запуском — как модули ядра.
 * Прогон таблицы случаев: `node tools/check-grammar.mjs` из папки `web/`.
 *
 * Закон сверки (решение 25.07 «грамматика двумя бесплатными ходами»):
 *   • пропуск в предложении требует ИМЕННО ту форму, что стоит в предложении
 *     (`examples.target_form`): `has _____` → `gone`, а НЕ `go`;
 *   • к регистру, лишним пробелам и точке в конце мы НЕ придираемся;
 *   • к форме — придираемся жёстко, иначе дрилл теряет смысл;
 *   • промах разбирается по типу: «то слово, но другая форма» / «описка» /
 *     «не то слово» — от типа зависит подсказка и вторая попытка.
 *
 * ⛔ Здесь нет ни SRS, ни сети, ни DOM: только строки. Данные (`forms`,
 * `example_gapped`, `example_form`) приходят готовыми из ядра — ничего не выдумываем.
 */

import type { Card } from "./api";

/** Маркер пропуска, который кладёт проход B (`enrich.py --cards`). */
export const GAP = "_____";

export type MissKind = "form" | "typo" | "miss";

export type Verdict =
  | { ok: true }
  | { ok: false; kind: MissKind; gap: number; expected: string; given: string };

/** Разобранный пример: куски текста (n+1) и ожидаемая форма на каждый пропуск (n). */
export interface GapSpec {
  chunks: string[];
  targets: string[];
}

/** Формы слова в примере: строка или список (разрывной оборот «was blown up»). */
export function formList(c: Card): string[] {
  const raw = c.example_form;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).filter((s) => s.trim());
  const s = String(raw).trim();
  if (s.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map(String).filter((x) => x.trim());
    } catch { /* не JSON — значит обычная строка */ }
  }
  return s ? [s] : [];
}

/**
 * Заготовка пропуска → куски + цели. Возвращает null, если «Контекст» на этой
 * карточке собрать не из чего (нет заготовки или нет ни одной формы) — тогда
 * занятие честно покажет карточку как «Досье», а не пустое поле.
 */
export function gapSpec(c: Card): GapSpec | null {
  const gapped = (c.example_gapped || "").trim();
  if (!gapped.includes(GAP)) return null;
  const chunks = gapped.split(GAP);
  const forms = formList(c);
  const fallback = (c.word || "").trim();
  const targets: string[] = [];
  for (let i = 0; i < chunks.length - 1; i++) {
    const t = (forms[i] ?? forms[0] ?? fallback).trim();
    if (!t) return null;
    targets.push(t);
  }
  return targets.length ? { chunks, targets } : null;
}

/**
 * ⛔ Здесь БОЛЬШЕ НЕТ правила «карточка вырождена» (было окно 2, снято окном 2.1 26.07).
 * Оно решало не ту задачу: подпирало подсказку-лемму у НОВОГО слова, а спрашивать форму
 * у слова, которое человек видит впервые, нельзя вообще — это продукция до узнавания.
 * Новый закон живёт там, где ему и место: «Контекст» с вводом даётся только
 * продукционной стороне (`direction === "ru_en"`, бокс 2+) — см. `screens/session.ts`,
 * `shownMode`. Отдельного закона в грамматике для этого не нужно.
 */

/**
 * Приведение к сравнимому виду: регистр, лишние пробелы, знаки препинания и разные
 * виды апострофа/тире. Внутренний апостроф (`don't`) и дефис остаются — они часть
 * слова, а не оформление.
 */
export function norm(s: string): string {
  return String(s ?? "")
    .toLowerCase()
    .replace(/[‘’ʼ`´]/g, "'")
    .replace(/[‐-―]/g, "-")
    .replace(/[.,!?;:"()[\]«»…]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Все известные написания этого слова: лемма, формы из `lexicon.forms`, формы примера. */
export function variants(c: Card): Set<string> {
  const out = new Set<string>();
  const push = (value?: string | null): void => {
    const v = norm(value || "");
    if (!v) return;
    out.add(v);
    if (v.includes(" ")) for (const token of v.split(" ")) out.add(token);
  };
  push(c.word);
  for (const v of Object.values(c.forms || {})) push(v as string);
  for (const f of formList(c)) push(f);
  return out;
}

/** Расстояние Левенштейна (нужно только чтобы отличить описку от другого слова). */
export function distance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length || !b.length) return Math.max(a.length, b.length);
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(
        prev[j] + 1,
        row[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = row;
  }
  return prev[b.length];
}

/** Описка — это когда до правильного ответа один-два символа, а не другое слово. */
function isTypo(given: string, expected: string): boolean {
  const limit = expected.length <= 5 ? 1 : 2;
  return distance(given, expected) <= limit;
}

/**
 * Сверка. Порядок разбора важен: сперва «это форма того же слова» (иначе
 * `claim` → `claims` уехало бы в «описку» и мы промолчали бы про грамматику),
 * потом описка, и лишь потом «не то слово».
 */
export function check(answers: string[], spec: GapSpec, c: Card): Verdict {
  const known = variants(c);
  for (let i = 0; i < spec.targets.length; i++) {
    const expected = spec.targets[i];
    const given = answers[i] ?? "";
    const a = norm(given);
    const b = norm(expected);
    if (a === b) continue;
    const kind: MissKind = !a
      ? "miss"
      : known.has(a) ? "form"
      : isTypo(a, b) ? "typo"
      : "miss";
    return { ok: false, kind, gap: i, expected, given: given.trim() };
  }
  return { ok: true };
}

/** Подсказка на вторую попытку: первая буква + длина («g _ _ _»). */
export function mask(target: string): string {
  return (target || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => [w[0], ...Array(Math.max(0, w.length - 1)).fill("_")].join(" "))
    .join("   ");
}

/** Подписи ключей `lexicon.forms` по-русски (для разбора после ответа). */
export const FORM_LABEL: Record<string, string> = {
  plural: "мн. ч.",
  past: "прошедшее",
  past_participle: "3-я форма",
  ing: "-ing",
  comparative: "сравнит.",
  superlative: "превосх.",
};

/* ── регулярные формы: что показывать не надо ─────────────────────────────────
   Замер 26.07 по боевой базе: формы есть у 687 слов, из них РЕГУЛЯРНЫХ 514 = 75%
   (`call round` → `called round` / `calling round`). Учить там нечего, а блок плашек
   на карточке создаёт кашу. Поэтому формы показываем только там, где правило НЕ
   работает (`go` → `went` / `gone`, `child` → `children`) — 172 слова.
   ⛔ Правило считается на ГОТОВЫХ данных: новых полей в базе не заводим.          */

const VOWEL = "aeiou";

/** Удвоение конечной согласной: `stop` → `stopped`, `hug` → `hugging`. */
function doubled(w: string): string[] {
  const n = w.length;
  if (n < 3) return [];
  const [a, b, c] = [w[n - 3], w[n - 2], w[n - 1]];
  if (VOWEL.includes(c) || "wxy".includes(c)) return [];   // на гласную и на w/x/y не удваивают
  if (!VOWEL.includes(b) || VOWEL.includes(a)) return [];  // нужен ровно один гласный перед концом
  return [w + c];
}

/** `-ed`: `call`→`called` · `like`→`liked` · `carry`→`carried` · `stop`→`stopped`. */
function edForms(w: string): Set<string> {
  const out = new Set<string>([w + "ed"]);
  if (w.endsWith("e")) out.add(w + "d");
  if (/[^aeiou]y$/.test(w)) out.add(w.slice(0, -1) + "ied");
  for (const d of doubled(w)) out.add(d + "ed");
  return out;
}

/** `-ing`: `call`→`calling` · `like`→`liking` · `lie`→`lying` · `run`→`running`. */
function ingForms(w: string): Set<string> {
  const out = new Set<string>([w + "ing"]);
  if (w.endsWith("ie")) out.add(w.slice(0, -2) + "ying");
  if (w.endsWith("e") && !/(ee|ye|oe)$/.test(w)) out.add(w.slice(0, -1) + "ing");
  for (const d of doubled(w)) out.add(d + "ing");
  return out;
}

/** `-s`/`-es`: `car`→`cars` · `box`→`boxes` · `city`→`cities` · `potato`→`potatoes`. */
function sForms(w: string): Set<string> {
  const out = new Set<string>([w + "s"]);
  if (/(s|x|z|ch|sh)$/.test(w)) out.add(w + "es");
  if (/[^aeiou]y$/.test(w)) out.add(w.slice(0, -1) + "ies");
  if (/[^aeiou]o$/.test(w)) out.add(w + "es");
  return out;
}

/** `-er`/`-est`: `big`→`bigger` · `nice`→`nicer` · `happy`→`happiest`. */
function degForms(w: string, suffix: "er" | "est"): Set<string> {
  const out = new Set<string>([w + suffix]);
  if (w.endsWith("e")) out.add(w + suffix.slice(1));
  if (/[^aeiou]y$/.test(w)) out.add(w.slice(0, -1) + "i" + suffix);
  for (const d of doubled(w)) out.add(d + suffix);
  return out;
}

const RULES: Record<string, (head: string) => Set<string>> = {
  past: edForms,
  past_participle: edForms,
  ing: ingForms,
  plural: sForms,
  third_person: sForms,
  comparative: (w) => degForms(w, "er"),
  superlative: (w) => degForms(w, "est"),
};

/**
 * Пустая клетка формы. В базе «нет такой формы» записано по-разному — прочерком,
 * «n/a», «—». Это не знание, а отсутствие данных: ни показывать, ни судить нечего.
 */
export function isBlankForm(value: unknown): boolean {
  const v = String(value ?? "").trim();
  return !v || /^[-–—‒―_.]+$/.test(v) || /^(n\/?a|none|нет|—)$/i.test(v);
}

/**
 * Выводится ли эта форма школьным правилом из леммы? У составных (`call round`,
 * `blow up`) изменяется ТОЛЬКО первое слово, хвост обязан совпасть — иначе форма
 * нерегулярна и её надо показать. Ключ, которого мы не знаем, судить не беремся:
 * такую форму считаем нерегулярной, то есть показываем (лучше лишняя, чем потерянная).
 */
export function isRegularForm(key: string, value: string, lemma: string): boolean {
  const rule = RULES[key];
  if (!rule) return false;
  // Аналитические степени длинных прилагательных (`more available`, `most available`)
  // — тоже правило, а не исключение: запоминать там нечего.
  if (key === "comparative" && norm(value) === "more " + norm(lemma)) return true;
  if (key === "superlative" && norm(value) === "most " + norm(lemma)) return true;
  const base = norm(lemma).split(" ");
  const got = norm(value).split(" ");
  if (!base[0] || !got[0]) return false;
  if (got.slice(1).join(" ") !== base.slice(1).join(" ")) return false;
  return rule(base[0]).has(got[0]);
}

/** Формы, которые правилом не выводятся (по ним и решаем, показывать ли блок). */
export function irregularKeys(c: Card): string[] {
  return Object.entries(c.forms || {})
    .filter(([k, v]) => !isBlankForm(v) && !isRegularForm(k, String(v), c.word || ""))
    .map(([k]) => k);
}

/** Есть чему учиться в формах этого слова? Нет — блок форм на карточке не рисуем. */
export function hasIrregularForms(c: Card): boolean {
  return irregularKeys(c).length > 0;
}
